import React, { useEffect, useState, useRef } from "react";
import { useApiSelect } from "../../../api/common/apiSelect";
import { useSelector } from 'react-redux';
import './CrudModal.css';
import Toggle from "../../Toggle/Toggle";
import { VALIDATORS } from "../../../utils/validators";
import { requiredMessage } from "../../../utils/koreanParticle";
import { useToast } from "../../../hooks/useToast";
import DatePickerField from "../../DatePickerField/DatePickerField";

// changedKey 변경 후, 그 키를 compute.from 에 가진 필드를 재계산. manuallyEdited 에 든 대상 필드는 보존.
export const applyComputes = (formFields, nextFormData, changedKey, manuallyEdited) => {
  const out = { ...nextFormData };
  for (const field of formFields) {
    const c = field.compute;
    if (!c || !Array.isArray(c.from)) continue;
    if (!c.from.includes(changedKey)) continue;
    if (manuallyEdited && manuallyEdited.has(field.key)) continue;
    out[field.key] = c.calc(out);
  }
  return out;
};

export const collectErrors = (formFields, formData) => {
  const errors = [];
  for (const field of formFields) {
    if (field.visible === false) continue;
    const val = formData?.[field.key];
    const isEmpty = field.type === "checkbox"
      ? (val === undefined || val === null)
      : (val === undefined || val === null || String(val).trim() === "");
    if (field.required && isEmpty) { errors.push(requiredMessage(field.label)); continue; }
    if (!isEmpty && field.format && VALIDATORS[field.format]) {
      const r = VALIDATORS[field.format](val);
      if (!r.valid) errors.push(r.message);
    }
  }
  return errors;
};

const CrudModal = ({ type, formFields, formDatas, onSubmit, onClose, clickedRow }) => {
  const { request } = useApiSelect();
  const session = useSelector((state) => state.session.data);
  const [options, setOptions] = useState({});
  const [formData, setFormData] = useState({});
  const [showDropdown, setShowDropdown] = useState({});
  const inputRefs = useRef({});
  const [errorMessage, setErrorMessage] = useState("");
  const [errors, setErrors] = useState([]);
  const toast = useToast();

  useEffect(() => {
    const initialData = formFields.reduce((acc, field) => {
      acc[field.key] =
        formDatas?.[field.key] ??
        field.value ??
        field.default ??
        '';
      return acc;
    }, {});
    setFormData(initialData);
  }, [formFields, formDatas]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      Object.keys(inputRefs.current).forEach((key) => {
        if (inputRefs.current[key] && !inputRefs.current[key].contains(event.target)) {
          setShowDropdown((prev) => ({ ...prev, [key]: false }));
        }
      });
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    const fetchOptions = async () => {
      const newOptions = {};
      for (const field of formFields) {
        if (field.type === "apiSelect" && field.apiKey && request[field.apiKey]) {
          const params = { companySeq: session.companySeq };
          if (Array.isArray(field.paramKeys)) {
            field.paramKeys.forEach((k) => {
              if (clickedRow && k in clickedRow) {
                params[k] = clickedRow[k];
              } else if (session && k in session) {
                params[k] = session[k];
              }
            });
          }
          const response = await request[field.apiKey](params);
          
          newOptions[field.key] = response.map((item) => ({
            label: item[field.labelKey],
            value: item[field.valueKey],
          }));
        }
      }
      setOptions(newOptions);
    };
    fetchOptions();
  }, [formFields]);

  const formatForDisplay = (raw) => {
  if (raw == null) return "";
  const s = String(raw);

  if (s === "" || s === "-" || s === "." || s === "-.") return s;

  const noComma = s.replace(/,/g, "");
  const neg = noComma.startsWith("-");
  const body = neg ? noComma.slice(1) : noComma;

  const hasDot = body.includes(".");
  const [intPartRaw, fracPartRaw = ""] = body.split(".");
  const intPart = intPartRaw.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  const out =
    (neg ? "-" : "") +
    intPart +
    (hasDot ? "." + fracPartRaw : "");

  return out;
};

const numericLike = (s) => /^-?\d*(\.\d*)?$/.test(s);

  const manuallyEdited = useRef(new Set());

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => {
      const next = { ...prev, [name]: value };
      // 사용자가 compute 대상 필드를 직접 편집 → 보존 표시
      if (formFields.some((f) => f.key === name && f.compute)) manuallyEdited.current.add(name);
      return applyComputes(formFields, next, name, manuallyEdited.current);
    });
  };

  const handleSubmit = async () => {
    const collected = collectErrors(formFields, formData);
    if (collected.length > 0) { setErrors(collected); setErrorMessage(""); return; }
    setErrors([]); setErrorMessage("");
    const submitData = formFields.reduce((acc, field) => { acc[field.key] = formData.hasOwnProperty(field.key) ? formData[field.key] : ''; return acc; }, {});
    try {
      const res = await onSubmit(submitData);
      if (res?.response?.status === 409) { setErrorMessage("공정코드가 이미 있습니다."); return; }
      toast.success(type === 1 ? "등록되었습니다" : "수정되었습니다");
      onClose();
    } catch (err) {
      const status = err?.response?.status ?? err?.status;
      const detail = err?.response?.data?.detail || err?.response?.data?.message;
      if (status === 409) { setErrorMessage("공정코드가 이미 있습니다."); return; }
      console.error(err);
      const msg = detail || "요청 처리 중 오류가 발생했습니다.";
      setErrorMessage(msg); toast.error(msg);
    }
  };

  return (
    <div className="crud-container">
      <div className="crud-body">
        {formFields.filter((input) => input.visible !== false).map((input, index) => (
          <div key={index} className="crud-input-group">
            {input.type === "apiSelect" ? (
              <div className="floating-label-group">
                <select
                  name={input.key}
                  id={input.key}
                  className="floating-input"
                  value={formData[input.key] || ""}
                  onChange={handleChange}
                >
                  <option value="">선택</option>
                  {options[input.key]?.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                <label htmlFor={input.key} className="floating-label">
                  {input.label}
                  {input.required && <span style={{ color: 'red', marginLeft: '4px' }}>*</span>}
                </label>
              </div>
            ) : input.type === "checkbox" ? (
              <div className="toggle-container">
                <Toggle
                  isOn={formData[input.key] !== undefined ? formData[input.key] : true}
                  handleToggle={() => {
                    const newValue = !(formData?.[input.key] ?? (input.default ?? true));
                    handleChange({ target: { name: input.key, value: newValue } });
                  }}
                  onText="사용"
                  offText="미사용"
                />
                <label>{input.label}{input.required && <span style={{ color: 'red', marginLeft: '4px' }}>*</span>}</label>
              </div>
            ) : input.type === "select" ? (
              <div className="floating-label-group">
                <select
                  name={input.key}
                  id={input.key}
                  className="floating-input"
                  value={formData[input.key] || ""}
                  onChange={handleChange}
                >
                  <option value="" disabled hidden></option>
                  {input.options?.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                <label htmlFor={input.key} className="floating-label">
                  {input.label}
                  {input.required && <span style={{ color: 'red', marginLeft: '4px' }}>*</span>}
                  </label>
              </div>
            ) : input.type === "date" ? (
              <DatePickerField id={input.key} name={input.key} label={input.label}
                value={formData[input.key]} onChange={handleChange} required={input.required} />
            ) : input.type === "number" ? (
              <div className="floating-label-group">
    <input
      type="text"
      name={input.key}
      id={input.key}
      value={formatForDisplay(formData[input.key] ?? "")}
      onChange={(e) => {
        const entered = e.target.value;
        const raw = entered.replace(/,/g, "");
        if (!numericLike(raw)) return;
        setFormData((prev) => {
          const next = { ...prev, [input.key]: raw };
          if (formFields.some((f) => f.key === input.key && f.compute)) manuallyEdited.current.add(input.key);
          return applyComputes(formFields, next, input.key, manuallyEdited.current);
        });
      }}
      required={input.required}
      placeholder=" "
      className="floating-input"
    />
    <label htmlFor={input.key} className="floating-label">
      {input.label}
      {input.required && <span style={{ color: 'red', marginLeft: 4 }}>*</span>}
    </label>
  </div>
            ): (
              <div className="floating-label-group">
                <input
                  type={input.type}
                  name={input.key}
                  id={input.key}
                  value={formData[input.key] || ""}
                  onChange={handleChange}
                  required={input.required}
                  placeholder=" "
                  className="floating-input"
                />
                <label htmlFor={input.key} className="floating-label">
                  {input.label}
                  {input.required && <span style={{ color: 'red', marginLeft: '4px' }}>*</span>}
                </label>

              </div>
            )}
          </div>
        ))}
      </div>
      <div className="crud-footer">
        {errors.length > 0 && (<ul className="error-message error-list">{errors.map((m,i)=><li key={i}>{m}</li>)}</ul>)}
        {errorMessage && (<div className="error-message">{errorMessage}</div>)}
        <div className="button-group">
          <button onClick={handleSubmit} className={`submit-button ${type === 1 ? "add" : "edit"}`}>
            {type === 1 ? "등록" : "수정"}
          </button>
          <button onClick={onClose} className="cancel-button">닫기</button>
        </div>
      </div>
    </div>
  );
};

export default CrudModal;
